import React, { useState, useMemo, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import UserMenu from "@/components/common/UserMenu";
import oneViewLogo from "@/assets/svg/alleralandingpageLogo.svg";
import oneSyncIcon from "@/assets/svg/ONEsyncIcon.svg";
import oneLinkIcon from "@/assets/svg/ONElinkIcon.svg";
import oneInsightIcon from "@/assets/svg/ONEinsightIcon.svg";
import oneKnowledgeIcon from "@/assets/svg/ONEknowledgeIcon.svg";
import supportIcon from "@/assets/svg/supportIcon.svg";
import enhancementIcon from "@/assets/svg/enhancementIcon.svg";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATH } from "@/constants/routeConstants";

type AppNavbarProps = {
  title?: string;
  rightContent?: React.ReactNode;
};

type NavItem = {
  key: string;
  label: string;
  href: string;
  iconSrc: string;
};

// Destinations are placeholders ("#") pending confirmed navigation targets from Alera.
const NAV_ITEMS: NavItem[] = [
  {
    key: "onesync",
    label: "ONEsync",
    href: "https://monitor.m.care",
    iconSrc: oneSyncIcon,
  },
  {
    key: "onelink",
    label: "ONElink",
    href: "https://monitor.m.care",
    iconSrc: oneLinkIcon,
  },
  {
    key: "oneinsight",
    label: "ONEinsight",
    href: "https://portal-alerahealth.com/",
    iconSrc: oneInsightIcon,
  },
  {
    key: "oneknowledge",
    label: "ONEknowledge",
    href: "https://alera-health.skyprepapp.com/account/signin?redirect_full_url=https%3A%2F%2Falera-health.skyprepapp.com%2Fadmin%2Fprogram%2Foverview",
    iconSrc: oneKnowledgeIcon,
  },
  {
    key: "enhancements",
    label: "Enhancements",
    href: "https://forms.cloud.microsoft/r/qGtRn50HY8",
    iconSrc: enhancementIcon,
  },
  {
    key: "support",
    label: "Support",
    href: "https://forms.cloud.microsoft/r/Z6xgg9jSiG",
    iconSrc: supportIcon,
  },
  
];

function getInitials(firstName?: string, lastName?: string): string {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  const firstInitial = first ? first[0] : "S";
  const lastInitial = last ? last[0] : "J";
  return `${firstInitial}${lastInitial}`.toUpperCase();
}

const AppNavbar: React.FC<AppNavbarProps> = ({ rightContent }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const initials = useMemo(
    () => getInitials(user?.first_name, user?.last_name),
    [user?.first_name, user?.last_name]
  );

  return (
    <>
      <header className='h-[65px] fixed top-0 left-0 right-0 z-50 border-b border-solid border-primary-200 dark:border-0 bg-[hsl(var(--navbar))] opacity-100 py-3 px-3 sm:px-4 md:px-[40px]'>
        <div className='w-full h-full flex items-center justify-between'>
          {/* Mobile Layout */}
          <div className='flex md:hidden items-center justify-between w-full'>
            {/* Left: Hamburger Menu and Logo */}
            <div className='flex items-center gap-3'>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className='p-2 bg-gray-100 hover:bg-gray-100 dark:bg-gray-800 rounded-md transition-colors bg-transparent dark:bg-transparent border-none focus:border-none focus:ring-0 hover:border-none hover:ring-0'
                aria-label='Toggle menu'>
                {isMobileMenuOpen ? (
                  <X className='w-6 h-6' />
                ) : (
                  <Menu className='w-6 h-6' />
                )}
              </button>

              {/* Logo */}
              <button
                type='button'
                onClick={() => {
                  navigate(ROUTE_PATH.ROUTE_DASHBOARD);
                  setIsMobileMenuOpen(false);
                }}
                className='cursor-pointer bg-transparent border-none p-0 m-0'
              >
                <img
                  src={oneViewLogo}
                  alt='ONEview'
                  className='dark:hidden'
                  style={{ height: "42px", width: "112px" }}
                  data-testid='mobile-logo'
                />
                <div
                  aria-hidden
                  className='hidden dark:block'
                  style={{
                    height: "42px",
                    width: "112px",
                    backgroundColor: "hsl(var(--logo))",
                    WebkitMaskImage: `url(${oneViewLogo})`,
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskSize: "contain",
                    WebkitMaskPosition: "center",
                    maskImage: `url(${oneViewLogo})`,
                    maskRepeat: "no-repeat",
                    maskSize: "contain",
                    maskPosition: "center",
                  }}
                />
              </button>
            </div>

            {/* Right: Avatar with initials and dropdown arrow */}
            <button
              ref={userMenuButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsUserMenuOpen(!isUserMenuOpen);
              }}
              className='flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-md bg-transparent dark:bg-transparent transition-colors border-none focus:border-none focus:ring-0'
              aria-label='User menu'
              data-testid='mobile-user-button'>
              <div className='w-8 h-8 bg-primary-700 rounded flex items-center justify-center'>
                <span className='text-sm font-semibold text-white select-none'>
                  {initials}
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${isUserMenuOpen ? "rotate-180" : "rotate-0"}`}
              />
            </button>
          </div>

          {/* Desktop Layout */}
          <div className='hidden md:flex items-center justify-between w-full'>
            {/* Logo */}
            <div className='flex items-center gap-6 md:gap-10 flex-shrink-0'>
              <button
                type='button'
                onClick={() => navigate(ROUTE_PATH.ROUTE_DASHBOARD)}
                className='cursor-pointer bg-transparent border-none p-0 m-0 flex items-center gap-6 md:gap-10'
              >
                <img
                  src={oneViewLogo}
                  alt='ONEview'
                  className='dark:hidden'
                  style={{ height: "40px", width: "160px" }}
                  data-testid='desktop-logo'
                />
                <div
                  aria-hidden
                  className='hidden dark:block'
                  style={{
                    height: "46px",
                    width: "184px",
                    backgroundColor: "hsl(var(--logo))",
                    WebkitMaskImage: `url(${oneViewLogo})`,
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskSize: "contain",
                    WebkitMaskPosition: "left center",
                    maskImage: `url(${oneViewLogo})`,
                    maskRepeat: "no-repeat",
                    maskSize: "contain",
                    maskPosition: "left center",
                  }}
                />
              </button>
            </div>

            {/* Product nav icons */}
            <div
              className='flex items-center gap-6 md:gap-4 ml-6 md:ml-10 flex-wrap md:flex-nowrap'
              data-testid='desktop-buttons'>
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.key}
                  type='button'
                  onClick={() =>
                    window.open(item.href, "_blank", "noopener,noreferrer")
                  }
                  className='flex flex-col items-center bg-transparent border-none cursor-pointer px-1'
                  aria-label={item.label}>
                  <img
                    src={item.iconSrc}
                    alt={item.label}
                    className='object-contain'
                    style={{
                      height: "40px",
                      width: "40px",
                    }}
                  />
                  <span className='whitespace-nowrap text-[14px] font-medium text-grey-800 dark:text-grey-100'>
                    {item.label}
                  </span>
                </button>
              ))}
            </div>

            {/* Right Content - Avatar changes for tablet vs desktop */}
            <div className='flex items-center gap-3 md:gap-4 flex-shrink-0 whitespace-nowrap'>
              {rightContent}
              {/* Tablet: Avatar with initials and dropdown */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsUserMenuOpen(!isUserMenuOpen);
                }}
                className='flex lg:hidden items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-md transition-colors outline-none focus:outline-none focus:ring-0 focus-visible:outline-none border-none'
                aria-label='User menu'
                data-testid='tablet-user-button'>
                <div className='w-8 h-8 bg-primary-700 rounded flex items-center justify-center'>
                  <span className='text-sm font-semibold text-white select-none'>
                    {initials}
                  </span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${isUserMenuOpen ? "rotate-180" : "rotate-0"}`}
                />
              </button>
              {/* Desktop: Full UserMenu */}
              <div className='hidden lg:block'>
                <UserMenu />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div
          className='md:hidden fixed top-[65px] left-0 right-0 bg-[hsl(var(--navbar))] border-b border-solid border-primary-200 dark:border-0 z-40 shadow-lg'
          data-testid='mobile-menu-dropdown'>
          <div className='flex flex-col p-4 gap-3'>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                type='button'
                onClick={() => {
                  window.open(item.href, "_blank", "noopener,noreferrer");
                  setIsMobileMenuOpen(false);
                }}
                className='w-full flex items-center gap-3 text-[14px] font-medium bg-transparent border border-solid border-primary-200 dark:border-grey-600 rounded-md px-3 py-2'
                aria-label={item.label}>
                <img
                  src={item.iconSrc}
                  alt={item.label}
                  className='w-5 h-5 object-contain'
                />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mobile & Tablet User Menu Dropdown - Shows name and options directly */}
      {isUserMenuOpen && (
        <div className='lg:hidden fixed top-[65px] right-0 bg-[hsl(var(--navbar))] border border-solid border-primary-200 dark:border-0 z-40 shadow-lg rounded-bl-lg min-w-[200px]'>
          <div className='p-4'>
            <UserMenu
              isMobile={true}
              onClose={() => setIsUserMenuOpen(false)}
              buttonRef={userMenuButtonRef}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AppNavbar;
